// @flow

import React, { useMemo } from 'react';
import { Callout, Intent } from '@blueprintjs/core';
import CategorizedBulkPickerDialog from '../pills/categorized-bulk-picker-dialog';
import type {
  Mode,
  Option,
} from '../pills/categorized-bulk-picker-dialog';
import TagPill from './tag-pill';
import type { TagTypeMeta } from './tag-types';

type Props = {
  isOpen: boolean,
  mode: Mode,
  selectionCount: number,
  tagTypes: TagTypeMeta[],
  allTags: $ReadOnlyArray<Object>,
  onClose: () => mixed,
  onConfirm: (tag: Object) => mixed,
};

const BulkTagPickerDialog = ({
  isOpen,
  mode,
  selectionCount,
  tagTypes,
  allTags,
  onClose,
  onConfirm,
}: Props) => {
  const categories = useMemo(
    () => tagTypes.map((tt) => ({ key: tt.key, label: tt.label, color: tt.color })),
    [tagTypes],
  );

  const options = useMemo(
    () =>
      allTags.map((t) => ({
        id: t.id,
        label: t.tagValue,
        categoryKey: t.tagType,
      })),
    [allTags],
  );

  const singleSelectCategories = useMemo(
    () => tagTypes.map((tt) => tt.key),
    [tagTypes],
  );

  const renderConfirmation = (option: Option, count: number, m: Mode) => {
    const tag = allTags.find((t) => t.id === option.id);
    if (!tag) return null;
    return (
      <div>
        <p>
          This will {m === 'add' ? 'add' : 'remove'}{' '}
          <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            <TagPill tag={tag} tagTypes={tagTypes} />
          </span>{' '}
          {m === 'add' ? 'to' : 'from'} {count} location
          {count === 1 ? '' : 's'}.
        </p>
        {m === 'add' && (
          <Callout intent={Intent.WARNING} icon="info-sign">
            Locations that already have a different{' '}
            <strong>{tag.tagType}</strong> tag will have it replaced.
          </Callout>
        )}
      </div>
    );
  };

  const handleConfirm = (option: Option) => {
    const tag = allTags.find((t) => t.id === option.id);
    if (tag) return onConfirm(tag);
    return undefined;
  };

  return (
    <CategorizedBulkPickerDialog
      isOpen={isOpen}
      mode={mode}
      selectionCount={selectionCount}
      categories={categories}
      options={options}
      singleSelectCategories={singleSelectCategories}
      copy={{
        addTitle: 'Bulk add tag',
        removeTitle: 'Bulk remove tag',
        addConfirmButton: 'Add tag',
        removeConfirmButton: 'Remove tag',
        categorySectionLabel: 'Tag type',
      }}
      renderConfirmation={renderConfirmation}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};

export default BulkTagPickerDialog;
